import uuid from 'node-uuid';
import moment from 'moment';
import { get, head } from 'lodash';
import {
	getPayeeBySortCodeAccountNumber,
	isPayingInternalAccountsWithoutBeneficiary,
	getPayeeBySortCodeAccountNumberName,
} from '../payees/selectors';
import { getReplacementDates, getFirstSubmissionDate } from '../calendar/selectors';
import {
	getPaymentIdsforSameDate,
	getSubmitConfirmedChapsPayments,
	getChapsFromAccount,
	getChapsFromAccounts,
	getPaymentsTotal as getChapsPaymentsTotal,
	getIsEditing,
	getChapsPaymentsList,
	getChapsPaymentsListToSubmitForDailyLimitReview,
	getChapsPaymentsListToSubmitForReview,
	getChapsBatchToSubmitForReview,
} from '../chaps/selectors';
import { isBatch } from '../batch/selectors';
import { initialiseBatch } from '../../states/batch/actions';
import {
	submitConfirmedBatch,
	getBatchDetails,
	submitConfirmedEditedBatch,
	submitBatchForReview,
	submitRepeatingPaymentForReview,
} from '../api/fasterPaymentActions';
import {
	getCHAPSPaymentToEdit,
	submitChapsPaymentsForDailyLimitReview,
	submitChapsPaymentsForReview,
	submitConfirmedChapsPayments,
	chapsEditPayment,
} from '../api/chapsActions';
import { fetchCalendar } from '../calendar/actions';
import { submitBeneficiaries } from '../payees/actions';
import { setSelectedFromAccount, setSelectedPaymentId } from '../payeeSelector/actions';
import { goBackPage, pages, goToPage } from '../page/actions';
import { getChapsRequest, getContextIdOrID } from '../../helpers/apiRequestBuilder';
import { calendarTypes } from '../../config';
import { isRepeatingPayment, getRepeatingPaymentToSubmitForReview } from '../payments/selectors';
import { getPayeeById } from '../commonSelectors/selectors';

import { getUserId } from '../user/selectors';
import { getBankId } from '../session/selectors';

import {
	getApiResponses,
	isConflictAndDailyLimitError,
	isConflictAndMaxTransactionLimitOrDailyLimitError,
} from '../../helpers/apiResponse';
import { fetchReviewRates } from '../rates/actions';
import { getMissingDetailsForChapsAndIFTEditPayment } from '../../utils/LCVMissingDetailsUtils';
import { getReference } from '../../helpers/fieldHelpers';

export const CHAPS_ADD_PAYMENTS = 'CHAPS/ADD_PAYMENTS';
export const CHAPS_ADD_SINGLE_PAYMENT = 'CHAPS/ADD_SINGLE_PAYMENT';
export const UPDATE_PAYMENT_ACCOUNT = 'CHAPS/UPDATE_PAYMENT_ACCOUNT';
export const UPDATE_PAYMENT_ACCOUNTS = 'CHAPS/UPDATE_PAYMENT_ACCOUNTS';
export const CHAPS_UPDATE_PAYMENT = 'CHAPS/UPDATE_PAYMENT';
export const CHAPS_DELETE_PAYMENT = 'CHAPS/DELETE_PAYMENT';
export const CHAPS_COPY_REF_TO_ALL = 'CHAPS/COPY_REF_TO_ALL';
export const CHAPS_COPY_DATE_TO_ALL = 'CHAPS/COPY_DATE_TO_ALL';
export const CHAPS_SET_AUTHORISERS = 'CHAPS/SET_AUTHORISERS';
export const CHAPS_UPDATE_PAYMENT_DETAILS = 'CHAPS/UPDATE_PAYMENT_DETAILS';
export const CREATE_NEW_CHAPS_TEMPLATE = 'CREATE_NEW_CHAPS_TEMPLATE';
export const CHAPS_TOGGLE_EDITING = 'CHAPS/TOGGLE_EDITING';
export const INIT_CHAPS = 'INIT_CHAPS';
export const CHAPS_SET_PAYMENT_TO_EDIT = 'CHAPS/SET_PAYMENT_TO_EDIT';
export const CHAPS_UPDATE_ALL_DATES = 'CHAPS_UPDATE_ALL_DATES';
export const CHAPS_UPDATE_ALL_FROM_ACCOUNTS = 'CHAPS_UPDATE_ALL_FROM_ACCOUNTS';

export const addSingleChapsPayment =
	({ fromAccount, id }) =>
	(dispatch, getState) => {
		const state = getState();
		return dispatch({
			type: CHAPS_ADD_SINGLE_PAYMENT,
			payment: [
				{
					id: id || uuid.v1(),
					fromAccount,
					date: getFirstSubmissionDate(state),
				},
			],
		});
	};

export const addMoreChapsPayment =
	({ fromAccount }) =>
	(dispatch) => {
		const id = uuid.v1();

		dispatch(addSingleChapsPayment({ fromAccount, id }));
		dispatch(setSelectedFromAccount(fromAccount));
		dispatch(setSelectedPaymentId(id));
		return dispatch(goToPage(pages.ChapsPayeeSelector));
	};

export const addChapsPayments =
	({ fromAccount, toAccounts, lastPaymentDate, toPayees }) =>
	(dispatch, getState) => {
		const state = getState();

		const payments = toAccounts.map((toAccount) => ({
			id: uuid.v1(),
			fromAccount,
			toAccount,
			reference: getReference(toPayees, toAccount, true),
			date: lastPaymentDate || getFirstSubmissionDate(state),
		}));

		if (payments.length) {
			const LAST_ARRAY_INDEX = 1;
			const { id = null } = payments[payments.length - LAST_ARRAY_INDEX];
			if (id) {
				dispatch(setSelectedPaymentId(id));
			}
		}

		return dispatch({
			type: CHAPS_ADD_PAYMENTS,
			payments,
		});
	};

export const chapsUpdatePaymentAccounts = ({ fromAccount, toAccounts, toAccount, toPayees }, paymentId) => ({
	type: UPDATE_PAYMENT_ACCOUNTS,
	fromAccount,
	toAccounts,
	toAccount,
	paymentId,
	toPayees,
});

export const chapsUpdateSinglePaymentAccount = ({ fromAccount, toAccount }, paymentId, reference) => ({
	type: UPDATE_PAYMENT_ACCOUNT,
	fromAccount,
	toAccount,
	paymentId,
	reference,
});

export const chapsUpdatePayment = ({ paymentId, formKey, value }) => ({
	type: CHAPS_UPDATE_PAYMENT,
	paymentId,
	formKey,
	value,
});

export const chapsUpdatePaymentDetails = (payment) => ({
	type: CHAPS_UPDATE_PAYMENT_DETAILS,
	payment,
});

export const chapsDeletePayment = (paymentId) => ({
	type: CHAPS_DELETE_PAYMENT,
	paymentId,
});

export const chapsCopyRefToAll = (paymentIds, reference) => ({
	type: CHAPS_COPY_REF_TO_ALL,
	paymentIds,
	reference,
});

export const chapsCopyDateToAll = (paymentIds, date) => ({
	type: CHAPS_COPY_DATE_TO_ALL,
	paymentIds,
	date,
});

export const submitChapsConfirmed = () => (dispatch, getState) => {
	const state = getState();
	const chapsPayments = getSubmitConfirmedChapsPayments(state);
	if (isBatch(state)) {
		const batchTotal = getChapsPaymentsTotal(state);
		const { batch, paymentCategory } = getBatchDetails(state, batchTotal);
		if (getIsEditing(state)) {
			return dispatch(
				submitConfirmedEditedBatch(
					chapsPayments,
					batch,
					getChapsFromAccount(state),
					getChapsFromAccounts(state),
					paymentCategory
				)
			);
		}
		return dispatch(submitConfirmedBatch(chapsPayments, batch, getChapsFromAccounts(state), paymentCategory, false));
	}
	const isEditMode = getIsEditing(state);
	if (isEditMode) {
		const payment = head(getChapsPaymentsList(state));
		const chapsRequest = getChapsRequest(payment);
		const sourceId = getContextIdOrID(payment, isEditMode);
		return dispatch(chapsEditPayment(payment, chapsRequest, state, sourceId)).then(() => {
			const state = getState();
			if (!state.chaps.deadlineMissed) dispatch(goToPage(pages.ChapsSubmitted));
		});
	}
	return dispatch(submitConfirmedChapsPayments(chapsPayments));
};

export const acceptDateChangeAndResubmit = (dispatch, getState) => {
	const state = getState();
	const { submission } = getReplacementDates(state);
	const paymentIds = getPaymentIdsforSameDate(state);
	dispatch(chapsCopyDateToAll(paymentIds, submission));
	return dispatch(submitChapsConfirmed());
};

export const goBackAfterDeadlineMissed = (submission) => (dispatch, getState) => {
	const state = getState();
	const paymentIds = getPaymentIdsforSameDate(state);
	dispatch(chapsCopyDateToAll(paymentIds, submission));
	return dispatch(fetchCalendar(calendarTypes.CALENDAR_TYPE_CHAPS)).then(() => dispatch(goBackPage()));
};

export const setChapsPaymentAuthorisers = (authorisers, paymentId) => ({
	type: CHAPS_SET_AUTHORISERS,
	paymentId,
	authorisers,
});

export const createNewChapsTemplate = (payment) => ({
	type: CREATE_NEW_CHAPS_TEMPLATE,
	payment,
});
export const toggleChapsEditing = () => ({ type: CHAPS_TOGGLE_EDITING });

export const initialiseChaps = ({ payments, date }) => {
	const p = payments.map((payment) => ({ ...payment, date }));
	const actual = {
		type: INIT_CHAPS,
		payments: p,
	};
	return actual;
};
export const chapsSetPaymentToEdit = (payment, isBatch) => ({
	type: CHAPS_SET_PAYMENT_TO_EDIT,
	payment: isBatch ? payment : [payment],
	meta: {
		allowedDuringApiCall: true,
	},
});

export const getChapsPaymentFromResponse = (response, { account }, state) => {
	const payeeWithSCAN = getPayeeBySortCodeAccountNumber(
		state,
		get(response, 'to_account.sort_code', ''),
		get(response, 'to_account.account_number', '')
	);
	const payeeWithName = getPayeeBySortCodeAccountNumberName(
		state,
		get(response, 'to_account.sort_code', ''),
		get(response, 'to_account.account_number', ''),
		get(response, 'to_account.name', '')
	);

	// Try to use payee with matching name, else fallback to first one with matching SCAN only
	const payee = payeeWithName ? payeeWithName : payeeWithSCAN;
	return {
		id: uuid.v1(),
		context_id: response.id,
		fromAccount: account,
		toAccount: get(payee, 'id', ''),
		date: moment(response.value_date),
		amount: get(response, 'amount.value', '').toString(),
		reference: response.reference,
	};
};

export const transformChapsBatchPayment = (payments) =>
	payments.map((rawPayment) => {
		const { date, fromAccount, toAccount, amount, reference } = rawPayment;
		return {
			id: uuid.v1(),
			fromAccount,
			toAccount,
			date: moment(date),
			amount,
			reference,
		};
	});

export const editCHAPSPayment =
	({ paymentToEdit, batchDetails, payments }) =>
	(dispatch, getState) => {
		const state = getState();
		const isBatch = get(batchDetails, 'isBatch', false);

		if (isBatch) {
			const chapsPayments = transformChapsBatchPayment(payments);
			dispatch(chapsSetPaymentToEdit(chapsPayments, isBatch));
			dispatch(initialiseBatch(batchDetails));
			return dispatch(goToPage(pages.ChapsPayments));
		}
		return dispatch(getCHAPSPaymentToEdit(paymentToEdit, state)).then((response) =>
			dispatch(getMissingDetailsForChapsAndIFTEditPayment(response.payload)).then(() => {
				const payment = getChapsPaymentFromResponse(get(response, 'payload'), paymentToEdit, getState());
				dispatch(chapsSetPaymentToEdit(payment, isBatch));
				return dispatch(goToPage(pages.ChapsPayments));
			})
		);
	};

export const updateAllChapsDates = ({ date }) => ({
	type: CHAPS_UPDATE_ALL_DATES,
	date,
});

export const updateAllChapsFromAccounts = ({ fromAccountId }) => ({
	type: CHAPS_UPDATE_ALL_FROM_ACCOUNTS,
	fromAccountId,
});

export const doChapsDailyLimitCheck = (dispatch, state) =>
	dispatch(submitChapsPaymentsForDailyLimitReview(getChapsPaymentsListToSubmitForDailyLimitReview(state)));

export const doChapsTransactionLimitCheck = (dispatch, state) => {
	const userId = getUserId(state);
	return dispatch(submitChapsPaymentsForReview(getChapsPaymentsListToSubmitForReview(state), userId));
};

export const handleTransactionLimitErrors = (dispatch, state) => (response) => {
	const apiResponses = getApiResponses(response);
	if (apiResponses.some(isConflictAndMaxTransactionLimitOrDailyLimitError)) {
		return null;
	}
	return dispatch(fetchReviewRates(state));
};

export const handleDailyLimitErrors = (dispatch, state) => (response) => {
	const apiResponses = getApiResponses(response);
	if (apiResponses.some(isConflictAndDailyLimitError)) {
		return null;
	}
	return doChapsTransactionLimitCheck(dispatch, state).then(handleTransactionLimitErrors(dispatch, state));
};

export const dispatchChapsReview = (dispatch, getState) => {
	const state = getState();
	const userId = getUserId(state);
	if (isBatch(state)) {
		const chapsPayments = getChapsPaymentsList(state);
		return dispatch(submitBatchForReview(getChapsBatchToSubmitForReview(state, chapsPayments), userId, true)).then(
			handleTransactionLimitErrors(dispatch, state)
		);
	}
	if (isRepeatingPayment(state)) {
		return dispatch(submitRepeatingPaymentForReview(getRepeatingPaymentToSubmitForReview(state), userId));
	}

	return doChapsDailyLimitCheck(dispatch, state).then(handleDailyLimitErrors(dispatch, state));
};

export const submitChapsReview = () => (dispatch, getState) => {
	const state = getState();
	const paymentsRequireBeneficiary = isPayingInternalAccountsWithoutBeneficiary(state, 'payment');

	if (paymentsRequireBeneficiary.length) {
		const bankId = getBankId(state);
		const { accounts } = state;

		return dispatch(submitBeneficiaries(bankId, paymentsRequireBeneficiary, accounts)).then(() =>
			dispatchChapsReview(dispatch, getState)
		);
	}

	return dispatchChapsReview(dispatch, getState);
};

export const postAddPayeeUpdateChapsPayment = (payment) => (dispatch, getState) => {
	const state = getState();
	const { fromAccount, toAccount } = payment;
	const { reference_high_value, reference } = getPayeeById(state, get(payment, 'toAccount'));
	return dispatch({
		type: CHAPS_ADD_SINGLE_PAYMENT,
		payment: [
			{
				date: getFirstSubmissionDate(state),
				fromAccount,
				id: uuid.v1(),
				reference: reference_high_value || reference,
				toAccount,
			},
		],
	});
};
